// ─── Feature Flags ────────────────────────────────────────────────────────────
//
// These flags control which features are visible to users.
// To enable a feature, change the value to `true` and redeploy.
//
// LANGUAGE_SWITCHER
//   false → Only English is shown. The language switcher is hidden from the UI.
//           All bilingual content (Chinese translations, zh question fields) is
//           preserved in the codebase and can be re-enabled at any time.
//   true  → Users see the EN | 中文 toggle in the header and can switch freely.
//
// NEW_WORDS / CHALLENGE_SUBMISSION
//   false → The New Word and Thought-Challenge (challenge submission) features
//           are hidden from the MVP: nav links, entry-point cards, and their
//           routes are removed (routes redirect home). ALL component + backend
//           code is preserved and re-enabled by flipping the flag (KAN-130 —
//           deferred to a later release).
//   true  → Features are fully visible and reachable.
//
// CCS (Cognitive Conditioning System — KAN-147)
//   false → The CCS bout runner route is hidden / redirects home.
//   true  → The /learn bout runner is reachable. Backend endpoints (/ccs/*)
//           and the page component are preserved regardless of the flag.
//
// LEARNING_THEORY (KAN-209)
//   false → The Learning-Theory submission module is hidden from the MVP: the
//           nav link, the entry-point CTA buttons, and the /learning-theory* +
//           /learning-theories/submit routes are removed (routes redirect home).
//           The homepage's static showcase of Novara's 8 foundational learning
//           theories is preserved (it is content, not the submission feature).
//           ALL component + backend code is preserved and re-enabled by the flag.
//   true  → The submission module (browse + submit user theories) is reachable.
export const FEATURE_FLAGS = {
  LANGUAGE_SWITCHER: false,
  NEW_WORDS: false,
  CHALLENGE_SUBMISSION: false,
  LEARNING_THEORY: false,
  CCS: true,
  // CCS_NATIVE_DIAGNOSTIC (KAN-243 follow-on) — the in-app FC diagnostic that reads
  //   item content from the server (key-stripped) and posts answers for server-side
  //   scoring, as an alternative to the prototype iframe.
  //   false → the onboarding flow goes straight to the constellation reveal (mid-Aug
  //           UAT: the diagnostic is delivered by the prototype). Route redirects home.
  //   true  → onboarding routes through /learn/diagnostic (native FC assessment) before
  //           the reveal.
  //   ON since 2026-09-02. The native path IS now the chosen path: Mr. Chen pulled Keyhole
  //   from the primary launch flow, and with this off the only route to a Future Capability
  //   assessment was the Keyhole prototype — so a new account could not reach FC in-app at
  //   all. This is server-scored (items key-stripped, answers scored server-side), which is
  //   also what the evidence chain needs; the prototype's scoring is client-side and
  //   advisory only.
  CCS_NATIVE_DIAGNOSTIC: true,
  // FC_UAT (Paul's Novara-FC UAT build)
  //   false → the /fc-uat route redirects home.
  //   true  → the /fc-uat route mounts the standalone UAT build in an iframe and
  //           captures its advisory `complete` payload. UAT only — client-side
  //           scoring, not measurement. Serve /fc/index.html from the same origin.
  FC_UAT: true,
  // FLARE_SHORT_SET (KAN-273 reworked entry flow, step 1 / KAN-279 capture)
  //   false -> the /learn/first-run route redirects home. Component, item data
  //           and tests ship regardless of the flag (same pattern as CCS).
  //   true  -> the short Flare set (5 items + voluntary continuation) is
  //           reachable as the in-app first experience and posts one session
  //           record to /api/flare/sessions. Session debrief only - the
  //           constellation stays dark until FC (conflict A, rec (b)).
  //   The /learn dashboard is flag-aware: off → it opens straight at the three
  //   options (proposal §5, "the Flare arrives as an addition"); on → the Flare
  //   is the one to-do until it is done. On for the reworked-flow build.
  // BRAIN_READINESS (the /learn check-in ritual, ported from the prototype's
  //   Calibration stage)
  //   false -> the dashboard opens straight at the to-do list.
  //   true  -> a press-and-hold brain scan sits above the list; one scan per day
  //           per user, kept in THIS BROWSER only (localStorage) until a
  //           server-side `readiness_check` field exists.
  //   Engagement layer, by construction: the score never touches placement,
  //   banding, measured_rank or the capability profile, and posts no telemetry.
  //   The scanner is theatre and says so; the three dials (clock, sleep,
  //   movement) are real and say that too.
  //   Ships dark: a new learner-facing surface, flipped on after review.
  BRAIN_READINESS: false,
  // JUDGMENT_SURFACES (KAN-455) — the /judgment-pipeline dashboard and the /mind-compiler
  //   intake form. Both are complete, protected, and run against registered routers.
  //   false -> both routes redirect home.
  //   true  -> both are reachable.
  //
  //   OFF, and the ruling is recorded on KAN-455 rather than assumed here. Two reasons, and
  //   neither is that the pages are unfinished:
  //
  //   1. Not one of the twelve Judgment acceptance items in the Minimum Version names an
  //      interface, screen, page or dashboard. They are records, storage, ranking, debate,
  //      memory and gating throughout. The document asks for no Judgment UI at launch.
  //   2. Mr. Chen's 2026-08-09 Judgment standards §X put the five-agent pipeline at Day 60
  //      and say a complex interface is not needed for launch.
  //
  //   Before this flag existed, both pages were reachable only by typing the URL — nothing in
  //   the product linked to either. That is not a disposition, it is an absence of one; this
  //   flag makes "not at launch" a decision the code carries. Flagged rather than retired
  //   because Day 60 wants the work.
  //
  //   The acceptance table being SILENT is evidence of what launch requires, not of what it
  //   forbids. If Ricardo wants either surface at launch for the committee, that is a product
  //   call and it beats this reading — flip the flag and say so on KAN-455.
  JUDGMENT_SURFACES: false,
  FLARE_SHORT_SET: true,
  // FLARE_SHORT_SET_BUILD — which item set /learn/first-run mounts (data/flare/index.js).
  //   'flare_short_set_v0_1' -> marbles / trains (the original short set)
  //   'flare_short_set_v0_2' -> the abstract set (darts, balances, repairs, gauges,
  //                             jugs, shelf, phone, tickets), same engine, same ramp.
  //   Both ship; flipping this is reversible and needs no data change.
  FLARE_SHORT_SET_BUILD: 'flare_short_set_v0_2',
  // INTERVIEW_LLM_PROBES (KAN-272 hook on the scripted /learn/interview surface)
  //   true  -> after each first answer the client asks POST /ccs/interview/probe
  //           for an adaptive follow-up (the scripted one rides along as intent).
  //           The SERVER flag settings.llm_interview_enabled governs whether the
  //           LLM actually fires; when it is off, or on any failure/timeout, the
  //           probe comes back null and the scripted follow-up is used - so this
  //           client flag is safe to leave on.
  //   false -> pure scripted follow-ups, no probe request at all.
  INTERVIEW_LLM_PROBES: true,
  // FC_SHARE (KAN-488, Ray 14449, 14 Oct: the FC assessment is standalone, and learners can
  //   share their results on social media)
  //   false -> the assessment's finished screen shows no share links (as before).
  //   true  -> it shows one-tap share links built from GET /api/share/card, which a completed
  //            or sealed FC attempt now earns. The outbound text is the server's (Paul's
  //            no-ranking text); the heading ("My Future Capability results") and the
  //            "Share my results" button are Paul's copy (KAN-488, 15 Oct;
  //            components/fc/FcShareLinks.js, FC_SHARE_COPY).
  //   On by default since Paul supplied the copy (15 Oct).
  FC_SHARE: true,
};
